import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Plus, Minus, RotateCcw } from 'lucide-react';

/**
 * Conteneur réutilisable apportant pinch-to-zoom (2 doigts) et pan (1 doigt)
 * sur mobile + molette de zoom + boutons +/-/reset sur desktop. Le contenu
 * conserve sa largeur naturelle et son rendu interne ; seule la matrice de
 * transformation est modifiée.
 */
const MIN_SCALE = 0.4;
const MAX_SCALE = 3;

const dist = (a, b) => {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
};
const mid = (a, b) => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

export default function PinchZoomPan({ children, showControls = true, className = '' }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const gesture = useRef({
    mode: null, // 'pinch' | 'pan'
    startScale: 1,
    startDist: 0,
    pinchCenter: { x: 0, y: 0 }, // dans le repère du conteneur
    startTx: 0,
    startTy: 0,
    startX: 0,
    startY: 0,
  });

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const clampScale = (s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

  const zoomBy = useCallback((delta, originX = null, originY = null) => {
    setScale((prev) => {
      const next = clampScale(prev + delta);
      if (next === prev) return prev;
      if (originX === null || originY === null) return next;
      // Garde le point sous le curseur stable lors du zoom
      const ratio = next / prev;
      setTx((cur) => originX - (originX - cur) * ratio);
      setTy((cur) => originY - (originY - cur) * ratio);
      return next;
    });
  }, []);

  // Wheel zoom (desktop)
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return; // zoom uniquement avec Ctrl/Cmd
      e.preventDefault();
      const rect = node.getBoundingClientRect();
      zoomBy(e.deltaY < 0 ? 0.1 : -0.1, e.clientX - rect.left, e.clientY - rect.top);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  // Touch gestures
  const onTouchStart = (e) => {
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const m = mid(a, b);
      gesture.current = {
        mode: 'pinch',
        startScale: scale,
        startDist: dist(a, b),
        pinchCenter: { x: m.x - rect.left, y: m.y - rect.top },
        startTx: tx,
        startTy: ty,
      };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      gesture.current = {
        mode: 'pan',
        startTx: tx,
        startTy: ty,
        startX: t.clientX,
        startY: t.clientY,
      };
    }
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    if (!g.mode) return;
    if (g.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const newDist = dist(a, b);
      if (g.startDist === 0) return;
      const newScale = clampScale(g.startScale * (newDist / g.startDist));
      const ratio = newScale / g.startScale;
      // Pour garder le centre de pinch fixe : t' = c - ratio * (c - t0)
      const newTx = g.pinchCenter.x - ratio * (g.pinchCenter.x - g.startTx);
      const newTy = g.pinchCenter.y - ratio * (g.pinchCenter.y - g.startTy);
      setScale(newScale);
      setTx(newTx);
      setTy(newTy);
    } else if (g.mode === 'pan' && e.touches.length === 1) {
      e.preventDefault();
      const t = e.touches[0];
      setTx(g.startTx + (t.clientX - g.startX));
      setTy(g.startTy + (t.clientY - g.startY));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length === 0) gesture.current = { mode: null };
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden touch-none select-none ${className}`}
      style={{ touchAction: 'none' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      data-testid="pinch-zoom-pan-wrapper"
    >
      <div
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: '0 0',
          transition: gesture.current.mode ? 'none' : 'transform 0.15s ease-out',
          willChange: 'transform',
        }}
      >
        {children}
      </div>

      {showControls && (
        <div className="absolute bottom-3 right-3 flex flex-col gap-2 z-10">
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="bg-white/90 backdrop-blur shadow-md"
            onClick={() => zoomBy(0.2)}
            data-testid="zoom-in-btn"
            aria-label="Zoom +"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="bg-white/90 backdrop-blur shadow-md"
            onClick={() => zoomBy(-0.2)}
            data-testid="zoom-out-btn"
            aria-label="Zoom -"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="bg-white/90 backdrop-blur shadow-md"
            onClick={reset}
            data-testid="zoom-reset-btn"
            aria-label="Réinitialiser"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
